import { z } from 'zod'
import { Address, BlockNumber, DecimalString, EvidenceId, Hash32, IsoTimestamp, UsdString, Uuid, WeiString } from '../primitives.js'
import { ExecutionState, OrderMode, PerfRange, PerformanceScope, PositionStatus, ProposalKind, ProposalStatus, QuoteExactness, QuoteSource, Verdict } from '../enums.js'
import { AgentSummary, TokenRef } from './common.js'

// Quote, proposal, verdict, execution ------------------------------------------

export const QuoteWitness = z.object({
  blockNumber: BlockNumber,
  blockHash: Hash32.optional(),
  at: IsoTimestamp,
})
export const Quote = z.object({
  amountOut: WeiString,
  minOut: WeiString,
  priceImpactBps: z.number().int().min(0),
  witness: QuoteWitness,
  venue: z.string(),
  poolId: z.string().optional(),
  /** Router contract the swap goes through. */
  router: Address,
  exactness: QuoteExactness,
})
export type Quote = z.output<typeof Quote>

export const Proposal = z.object({
  id: Uuid,
  agentId: Uuid,
  agentVersionId: Uuid,
  /** The customer-signed permission (`accounts.permissions`); null for proposals from before the cut. */
  permissionRef: z.string().nullable(),
  kind: ProposalKind,
  tokenIn: Address,
  tokenOut: Address,
  amountIn: WeiString,
  minOut: WeiString.nullable(),
  quote: Quote.nullable(),
  quoteSource: QuoteSource.nullable(),
  rationale: z.string(),
  evidenceId: EvidenceId,
  idempotencyKey: z.string(),
  status: ProposalStatus,
  refusalCodes: z.array(z.string()).nullable(),
  createdAt: IsoTimestamp,
  expiresAt: IsoTimestamp,
  decidedAt: IsoTimestamp.nullable(),
})
export type Proposal = z.output<typeof Proposal>

const CheckValue = z.union([z.string(), z.number(), z.boolean(), z.null()])
export const PolicyCheck = z.object({
  name: z.string(),
  ok: z.boolean(),
  observed: CheckValue,
  limit: CheckValue,
})
export type PolicyCheck = z.output<typeof PolicyCheck>

export const PolicyVerdict = z.object({
  id: Uuid,
  proposalId: Uuid,
  verdict: Verdict,
  /** Refusal codes from packages/policy (see `POLICY_REFUSAL_CODES`); empty when approved. */
  codes: z.array(z.string()),
  checks: z.array(PolicyCheck),
  evaluatedBy: z.string(),
  evidenceId: EvidenceId,
  createdAt: IsoTimestamp,
})
export type PolicyVerdict = z.output<typeof PolicyVerdict>

/**
 * The arbitrage order a proposal became (the platform holds it; algo.boo's mirror `trading.orders` went with migration
 * 0061). `amountIn` and
 * `amountOut` are what filled; `state` is arbitrage's order state (or `submitting` before the acknowledgement).
 */
export const Execution = z.object({
  id: Uuid,
  proposalId: Uuid.nullable(),
  /** `accounts.wallets.id` of the wallet the order traded. */
  walletId: Uuid.nullable(),
  /** arbitrage `ord_…`; null before the acknowledgement and for orders refused at accept. */
  orderRef: z.string().nullable(),
  clientOrderId: z.string(),
  mode: OrderMode,
  /** arbitrage ran it as a dry run (requested, or unarmed). */
  dryRun: z.boolean(),
  state: ExecutionState,
  refusalCode: z.string().nullable(),
  txHash: Hash32.nullable(),
  blockNumber: BlockNumber.nullable(),
  amountIn: WeiString.nullable(),
  amountOut: WeiString,
  feeAmountWei: WeiString.nullable(),
  feeAmountUsd: UsdString.nullable(),
  notionalUsd: UsdString.nullable(),
  executedAt: IsoTimestamp.nullable(),
  evidenceId: EvidenceId,
  createdAt: IsoTimestamp,
  updatedAt: IsoTimestamp,
})
export type Execution = z.output<typeof Execution>

/** Proposal + verdict + execution summary (GET /agents/:handle/decisions). */
export const Decision = z.object({
  proposal: Proposal,
  verdict: PolicyVerdict.nullable(),
  execution: Execution.nullable(),
})
export type Decision = z.output<typeof Decision>

/** An executed swap (GET /agents/:handle/trades). */
export const Trade = z.object({
  /** The order's id (the platform's; algo.boo's `trading.orders` went with migration 0061). */
  executionId: Uuid,
  proposalId: Uuid.nullable(),
  agent: AgentSummary,
  kind: ProposalKind,
  tokenIn: TokenRef,
  tokenOut: TokenRef,
  amountIn: WeiString,
  amountOut: WeiString,
  notionalUsd: UsdString.nullable(),
  txHash: Hash32.nullable(),
  blockNumber: BlockNumber.nullable(),
  state: ExecutionState,
  mode: OrderMode,
  dryRun: z.boolean(),
  feeAmountUsd: UsdString.nullable(),
  executedAt: IsoTimestamp,
  evidenceId: EvidenceId,
})
export type Trade = z.output<typeof Trade>

export const Position = z.object({
  id: Uuid.nullable(),
  token: TokenRef,
  /** The paper sleeve's cash (USD stable) position; shown as "Cash". */
  isCash: z.boolean(),
  quantity: WeiString,
  avgCostUsd: UsdString,
  currentValueUsd: UsdString.nullable(),
  realizedPnlUsd: UsdString,
  unrealizedPnlUsd: UsdString.nullable(),
  status: PositionStatus,
  openedAt: IsoTimestamp,
  closedAt: IsoTimestamp.nullable(),
})
export type Position = z.output<typeof Position>

export const PerformancePoint = z.object({
  asOf: IsoTimestamp,
  equityUsd: UsdString,
  pnlUsd: UsdString,
})
/** One book of the platform's performance, in ETH as exact decimals of its wei (lane ALGOBOO1, PERF1 and PERF2). */
export const PerformanceBookSummary = z.object({
  trades: z.number().int().nonnegative(),
  winRate: DecimalString.nullable(),
  openPositions: z.number().int().nonnegative(),
  /** Net P&L of the range (realized and unrealized, less gas and fees); null where the book has no history that far back. */
  pnlEth: DecimalString.nullable(),
  /** Net P&L of the book's whole history. */
  pnlAllEth: DecimalString.nullable(),
  gasEth: DecimalString,
  feesEth: DecimalString,
  volumeEth: DecimalString,
  maxDrawdownEth: DecimalString.nullable(),
  firstTradeAt: IsoTimestamp.nullable(),
  lastTradeAt: IsoTimestamp.nullable(),
})
export type PerformanceBookSummary = z.output<typeof PerformanceBookSummary>

export const PerformanceSeries = z.object({
  agentId: Uuid,
  range: PerfRange,
  /**
   * The book the series comes from (paper or live, never a mix); null when the agent has no snapshot yet. The API
   * always sends it; optional only until the web test stub sends it (docs/DEVIATIONS.md "reconciliation").
   */
  scope: PerformanceScope.nullable().optional(),
  points: z.array(PerformancePoint),
  /**
   * The platform holds the books (lane BOO2): every figure is null and `points` is empty until it serves a strategy's
   * performance; `unavailable` says why in plain words (docs/BACKLOG.md BOO2 "needs platform route").
   */
  summary: z.object({
    pnlUsd: UsdString.nullable(),
    equityUsd: UsdString.nullable(),
    maxDrawdownPct: DecimalString.nullable(),
    winRate: DecimalString.nullable(),
    tradesCount: z.number().int().nonnegative().nullable(),
    openPositions: z.number().int().nonnegative().nullable(),
  }),
  unavailable: z.string().nullable().optional(),
  /**
   * The platform's performance of the agent's strategy (`GET /v2/strategies/{ref}/performance`, lane ALGOBOO1):
   * each book apart, the one `scope` names first; `pointsEth` its cumulative realized result per day an exit filled in the range, oldest first. The
   * USD figures above stay null: the platform counts in wei of ETH and no price is invented.
   */
  books: z.object({ paper: PerformanceBookSummary.nullable(), live: PerformanceBookSummary.nullable() }).optional(),
  pointsEth: z.array(z.object({ asOf: IsoTimestamp, pnlEth: DecimalString })).optional(),
})
export type PerformanceSeries = z.output<typeof PerformanceSeries>

/**
 * One agent's week under the platform's ONE ranking rule (lane BOOSOCIAL1, arbitrage's decision O2): read from the App
 * API's standings (`app-strategy-standings/1`) and never computed here. `rank` is the agent's place among the agents
 * listed, in the platform's order; null with `unranked` saying why (fewer than five closed round trips, or its figure
 * not reported). `netEth` is the platform's week's net (realized less gas and fees), exact, null when not reported.
 */
export const WeekStanding = z.object({
  rank: z.number().int().positive().nullable(),
  unranked: z.enum(['fewRoundTrips', 'notReported']).nullable(),
  /** Why it is unranked, in plain words; null when ranked. */
  unrankedWords: z.string().nullable(),
  agent: AgentSummary,
  netEth: DecimalString.nullable(),
  roundTrips: z.number().int().nonnegative(),
  trades: z.number().int().nonnegative(),
  /** The days the figure covers: seven, or fewer for an agent younger than the week ("N days"). */
  days: z.number().int().min(1).max(7).nullable(),
  simulated: z.boolean(),
  /**
   * Beside the rank, never part of it (lane BOOCLEAN1): the agent's followers, its token's holders, its win rate and its
   * AUM as algo.boo keeps them (`agents.agent_stats`); the win rate and the AUM are null while the platform does not
   * serve them.
   */
  followersCount: z.number().int().nonnegative(),
  holdersCount: z.number().int().nonnegative(),
  winRate: DecimalString.nullable(),
  aumUsd: UsdString.nullable(),
})
export type WeekStanding = z.output<typeof WeekStanding>

export const WeekStandingsList = z.object({ ranked: z.array(WeekStanding), unranked: z.array(WeekStanding) })

/** `GET /standings/week`: the week's top agents, live and paper apart (paper labelled simulated), the rule in words. */
export const WeekStandings = z.object({
  asOf: IsoTimestamp.nullable(),
  rule: z.string(),
  minRoundTrips: z.number().int().positive(),
  live: WeekStandingsList,
  paper: WeekStandingsList.extend({ simulated: z.literal(true) }),
})
export type WeekStandings = z.output<typeof WeekStandings>
